import { Injectable, signal } from '@angular/core';
import { Activity } from '@mira-avant/mtlibs';
import { Client } from '@mira-avant/mtlibs';
import { Content } from '@mira-avant/mtlibs';
import { Course } from '@mira-avant/mtlibs';
import { Lesson } from '@mira-avant/mtlibs';
import { Unit } from '@mira-avant/mtlibs';
import { AdminService } from './admin.service';
import { ClientService } from './client.service';
import { CourseService } from './course.service';
import { UnitService } from './unit.service';
import { LessonService } from './lesson.service';
import { ActivityService } from './activity.service';
import { ContentService } from './content.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { combineLatest } from 'rxjs/internal/observable/combineLatest';

@Injectable({
	providedIn: 'root',
})
export class ClipboardService {
	public clients = signal<Client[]>([]);
	public courses = signal<Course[]>([]);
	public units = signal<Unit[]>([]);
	public lessons = signal<Lesson[]>([]);
	public activities = signal<Activity[]>([]);
	public contents = signal<Content[]>([]);
	private sub: Subscription | undefined;

	public readonly canPasteClients = signal<boolean>(false);
	public readonly canPasteCourses = signal<boolean>(false);
	public readonly canPasteUnits = signal<boolean>(false);
	public readonly canPasteLessons = signal<boolean>(false);
	public readonly canPasteActivities = signal<boolean>(false);
	public readonly canPasteContents = signal<boolean>(false);

	constructor(
		private adminService: AdminService,
		private clientService: ClientService,
		private courseService: CourseService,
		private unitService: UnitService,
		private lessonService: LessonService,
		private activityService: ActivityService,
		private contentService: ContentService
	) {
		this.sub = combineLatest([
			this.adminService.lowestSelectedUuid$,
			this.adminService.selectedClient$,
			this.adminService.selectedCourse$,
			this.adminService.selectedUnit$,
			this.adminService.selectedLesson$,
			this.adminService.selectedActivity$,
		]).subscribe(() => {
			this.canPasteClients.set(this.adminService.lowestSelectedUuid$.value === undefined);
			this.canPasteCourses.set(
				this.adminService.selectedClient$.value !== undefined &&
					this.adminService.lowestSelectedUuid$.value === this.adminService.selectedClient$.value.uuid
			);
			this.canPasteUnits.set(
				this.adminService.selectedCourse$.value !== undefined &&
					this.adminService.lowestSelectedUuid$.value === this.adminService.selectedCourse$.value.uuid
			);
			this.canPasteLessons.set(
				this.adminService.selectedUnit$.value !== undefined &&
					this.adminService.lowestSelectedUuid$.value === this.adminService.selectedUnit$.value.uuid
			);
			this.canPasteActivities.set(
				this.adminService.selectedLesson$.value !== undefined &&
					this.adminService.lowestSelectedUuid$.value === this.adminService.selectedLesson$.value.uuid
			);
			this.canPasteContents.set(
				this.adminService.selectedActivity$.value !== undefined &&
					this.adminService.lowestSelectedUuid$.value === this.adminService.selectedActivity$.value.uuid
			);
		});
	}

	public copyClient(client: Client): void {
		//If this client is not already in the clipboard add it
		if (!this.clients().includes(client)) {
			this.clients.set([...this.clients(), client]);
		}
	}

	public copyClients(clients: Client[]): void {
		for (const client of clients) {
			if (!this.clients().includes(client)) {
				this.clients.set([...this.clients(), client]);
			}
		}
	}

	public copyCourse(course: Course): void {
		if (!this.courses().includes(course)) {
			this.courses.set([...this.courses(), course]);
		}
	}

	public copyCourses(courses: Course[]): void {
		for (const course of courses) {
			if (!this.courses().includes(course)) {
				this.courses.set([...this.courses(), course]);
			}
		}
	}

	public copyUnit(unit: Unit): void {
		if (!this.units().includes(unit)) {
			this.units.set([...this.units(), unit]);
		}
	}

	public copyUnits(units: Unit[]): void {
		for (const unit of units) {
			if (!this.units().includes(unit)) {
				this.units.set([...this.units(), unit]);
			}
		}
	}

	public copyLesson(lesson: Lesson): void {
		if (!this.lessons().includes(lesson)) {
			this.lessons.set([...this.lessons(), lesson]);
		}
	}

	public copyLessons(lessons: Lesson[]): void {
		for (const lesson of lessons) {
			if (!this.lessons().includes(lesson)) {
				this.lessons.set([...this.lessons(), lesson]);
			}
		}
	}

	public copyActivity(activity: Activity): void {
		if (!this.activities().includes(activity)) {
			this.activities.set([...this.activities(), activity]);
		}
	}

	public copyActivities(activities: Activity[]): void {
		for (const activity of activities) {
			if (!this.activities().includes(activity)) {
				this.activities.set([...this.activities(), activity]);
			}
		}
	}

	public copyContent(content: Content): void {
		if (!this.contents().includes(content)) {
			this.contents.set([...this.contents(), content]);
		}
	}

	public copyContents(contents: Content[]): void {
		for (const content of contents) {
			if (!this.contents().includes(content)) {
				this.contents.set([...this.contents(), content]);
			}
		}
	}

	// Paste methods remain unchanged (they still use the old .value access on the BehaviorSubjects)
	public async pasteClients(): Promise<void> {
		if (!this.clients().length) {
			throw new Error('No clients to paste');
		}

		await this.clientService.duplicateBulk$(this.clients().map(client => client.uuid));
		this.clearClients();
	}

	public async pasteCourses(): Promise<void> {
		if (!this.courses().length) {
			throw new Error('No courses to paste');
		}

		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client to paste course to');
		}

		await this.courseService.duplicateBulk$(
			this.courses().map(course => course.uuid),
			this.adminService.selectedClient$.value.uuid
		);

		this.clearCourses();
	}

	public async pasteUnits(): Promise<void> {
		if (!this.units().length) {
			throw new Error('No units to paste');
		}

		if (!this.adminService.selectedCourse$.value) {
			throw new Error('No course to paste unit to');
		}

		await this.unitService.duplicateBulk$(
			this.units().map(unit => unit.uuid),
			this.adminService.selectedCourse$.value.uuid
		);

		this.clearUnits();
	}

	public async pasteLessons(): Promise<void> {
		if (!this.lessons().length) {
			throw new Error('No lessons to paste');
		}

		if (!this.adminService.selectedUnit$.value) {
			throw new Error('No unit to paste lesson to');
		}

		await this.lessonService.duplicateBulk$(
			this.lessons().map(lesson => lesson.uuid),
			this.adminService.selectedUnit$.value.uuid
		);

		this.clearLessons();
	}

	public async pasteActivities(): Promise<void> {
		if (!this.activities().length) {
			throw new Error('No activities to paste');
		}

		if (!this.adminService.selectedLesson$.value) {
			throw new Error('No lesson to paste activity to');
		}

		await this.activityService.duplicateBulk$(
			this.activities().map(activity => activity.uuid),
			this.adminService.selectedLesson$.value.uuid
		);

		this.clearActivities();
	}

	public async pasteContents(): Promise<void> {
		if (!this.contents().length) {
			throw new Error('No contents to paste');
		}

		if (!this.adminService.selectedActivity$.value) {
			throw new Error('No activity to paste content to');
		}

		await this.contentService.duplicateBulk$(
			this.contents().map(content => content.uuid),
			this.adminService.selectedActivity$.value.uuid
		);

		this.clearContents();
	}

	public clearClient(uuid: string): void {
		const currentClients = this.clients().filter(client => client.uuid !== uuid);
		this.clients.set(currentClients);
	}

	public clearClients(): void {
		this.clients.set([]);
	}

	public clearCourse(uuid: string): void {
		const currentCourses = this.courses().filter(course => course.uuid !== uuid);
		this.courses.set(currentCourses);
	}

	public clearCourses(): void {
		this.courses.set([]);
	}

	public clearUnit(uuid: string): void {
		const currentUnits = this.units().filter(unit => unit.uuid !== uuid);
		this.units.set(currentUnits);
	}

	public clearUnits(): void {
		this.units.set([]);
	}

	public clearLesson(uuid: string): void {
		const currentLessons = this.lessons().filter(lesson => lesson.uuid !== uuid);
		this.lessons.set(currentLessons);
	}

	public clearLessons(): void {
		this.lessons.set([]);
	}

	public clearActivity(uuid: string): void {
		const currentActivities = this.activities().filter(activity => activity.uuid !== uuid);
		this.activities.set(currentActivities);
	}

	public clearActivities(): void {
		this.activities.set([]);
	}

	public clearContent(uuid: string): void {
		const currentContents = this.contents().filter(content => content.uuid !== uuid);
		this.contents.set(currentContents);
	}

	public clearContents(): void {
		this.contents.set([]);
	}
}
